import React from "react";
import { observable, action } from "mobx";
import * as Api from "api";
import * as User from "api/User";

const ACCESS_TOKEN_KEY = "accessToken";

export class AuthStore {
  @observable.ref
  public verifying = true;

  @observable.ref
  public currentUser?: User.VerifyApiResponse;

  @action.bound
  public signIn(accessToken?: string) {
    const token = (() => {
      if (accessToken) {
        localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
        return accessToken;
      }

      return localStorage.getItem(ACCESS_TOKEN_KEY) || "";
    })();

    Api.request.setConfig({
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    this.verify();
  }

  @action.bound
  public signOut() {
    this.currentUser = undefined;
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    Api.request.setConfig({
      headers: {
        "Content-Type": "application/json",
        Authorization: "",
      },
    });
  }

  @action.bound
  public async verify() {
    this.verifying = true;
    const response = await User.Api.verify();
    this.verifying = false;

    if (response?.status === Api.Status.OK) {
      this.currentUser = response.data;
      return true;
    }

    return false;
  }

  public admin() {
    return !this.currentUser?.agency && !this.currentUser?.developer;
  }
}

const Context = React.createContext<AuthStore | null>(null);

export const AuthStoreProvider: React.FC<{ value?: AuthStore }> = ({
  children,
  value,
}) => {
  const store = React.useMemo(() => (value ? value : new AuthStore()), [value]);
  return <Context.Provider value={store}>{children}</Context.Provider>;
};

export const useAuthStore = () => {
  const store = React.useContext(Context);
  if (!store) {
    throw new Error("useAuthStore must be used within a AuthStoreProvider.");
  }
  return store;
};
